import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Code2, Users, MessageCircle, Activity, ArrowLeft, TrendingUp } from 'lucide-react';
import { createSoftwareSlug } from '../utils/urlHelpers';
import { usePageSeo } from '../hooks/usePageSeo';
import { absoluteUrl, applyPageSeo } from '../utils/seo';
import SoftwarePageHero from './SoftwarePageHero';
import { aggregateCategoryForums, latestForumUpdateIso } from '../utils/categoryAggregates';
import { buildSoftwareListSeoParagraph } from '../utils/softwareAggregates';
import type { ForumBase } from '../types/forum';
interface SoftwareStats {
  software: string;
  forum_count: number;
  total_topics: number;
  total_posts: number;
  total_members: number;
}

interface Props {
  darkMode: boolean;
}

const SoftwareStats: React.FC<Props> = ({ darkMode }) => {
  const [stats, setStats] = useState<SoftwareStats[]>([]);
  const [forums, setForums] = useState<ForumBase[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  usePageSeo({
    title: 'Forum Yazılımları ve İstatistikleri',
    description:
      'Türk forumlarında kullanılan forum yazılımlarının istatistikleri, kullanım oranları ve detaylı karşılaştırmaları.',
    keywords:
      'forum yazılımları, vBulletin, XenForo, phpBB, MyBB, forum software, forum istatistikleri',
    canonical: absoluteUrl('/software'),
  });

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (loading || forums.length === 0) return;
    const agg = aggregateCategoryForums(forums);
    const desc = buildSoftwareListSeoParagraph(stats.length, agg, latestForumUpdateIso(forums));
    applyPageSeo({
      title: 'Forum Yazılımları ve İstatistikleri',
      description: desc.slice(0, 320),
      keywords:
        'forum yazılımları, vBulletin, XenForo, phpBB, MyBB, forum software, forum istatistikleri',
      canonical: absoluteUrl('/software'),
    });
  }, [loading, forums, stats.length]);

  const fetchStats = async () => {
    try {
      const [{ data, error }, { data: forumsData, error: forumsError }] = await Promise.all([
        supabase.from('forum_software_stats').select('*').order('forum_count', { ascending: false }),
        supabase.from('forums').select('*').eq('is_active', true).not('software', 'is', null),
      ]);

      if (error) throw error;
      if (forumsError) throw forumsError;
      setStats(data || []);
      setForums(forumsData || []);
    } catch (error) {
      console.error('Error fetching software stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const calculateMarketShare = (forumCount: number): string => {
    const totalForums = stats.reduce((sum, stat) => sum + stat.forum_count, 0);
    return ((forumCount / totalForums) * 100).toFixed(1) + '%';
  };

  const handleSoftwareClick = (software: string) => {
    navigate(`/software/${createSoftwareSlug(software)}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-7xl">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-6 ${
            darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft size={20} />
          Geri Dön
        </Link>

        <header className="text-center mb-8">
          <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-violet-900'}`}>
            Forum Yazılımları
          </h1>
          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} max-w-2xl mx-auto`}>
            Türk forumlarında kullanılan yazılımların istatistikleri ve analizleri
          </p>
        </header>

        <SoftwarePageHero stats={stats} forums={forums} darkMode={darkMode} />

        <div className="grid gap-6">
          {stats.map((stat) => (
            <div
              key={stat.software}
              className={`${
                darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
              } rounded-xl p-6 shadow-lg cursor-pointer transition-colors duration-200`}
              onClick={() => handleSoftwareClick(stat.software)}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <Code2 className="text-indigo-500" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{stat.software}</h2>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {stat.forum_count} forum · {calculateMarketShare(stat.forum_count)} pazar payı
                    </p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  darkMode ? 'bg-gray-700' : 'bg-gray-100'
                }`}>
                  <TrendingUp size={18} className="text-green-500" />
                  <span className="font-medium">
                    {formatNumber(Math.round(stat.total_posts / stat.forum_count))} ileti/forum
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="text-purple-500" size={20} />
                    <h3 className="font-medium">Toplam Konu</h3>
                  </div>
                  <p className="text-2xl font-bold">{formatNumber(stat.total_topics)}</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {formatNumber(Math.round(stat.total_topics / stat.forum_count))} konu/forum
                  </p>
                </div>

                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="text-green-500" size={20} />
                    <h3 className="font-medium">Toplam İleti</h3>
                  </div>
                  <p className="text-2xl font-bold">{formatNumber(stat.total_posts)}</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {formatNumber(Math.round(stat.total_posts / stat.total_topics))} ileti/konu
                  </p>
                </div>

                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="text-blue-500" size={20} />
                    <h3 className="font-medium">Toplam Üye</h3>
                  </div>
                  <p className="text-2xl font-bold">{formatNumber(stat.total_members)}</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {formatNumber(Math.round(stat.total_members / stat.forum_count))} üye/forum
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SoftwareStats;